import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PortalNavbarComponent } from './portal-navbar.component';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { QuestionDto, QuestionService } from '../services/question.service';
import { AuthService } from '../services/auth.service';

type UtilityQuestionAction = 'restore' | 'hardDelete';

@Component({
  selector: 'app-utilities-questions-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/home" class="home-btn">Home</a>
          <h1 class="page-title">Utilities · Questions Data Maintenance</h1>
          <p class="page-sub">Restore soft-deleted questions or permanently remove them.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="card">
            <div class="card-hd">
              <span class="card-title">Questions</span>
              <div class="card-tools">
                <label class="tool-toggle">
                  <input type="checkbox" [(ngModel)]="showOnlyDeleted" />
                  Deleted only
                </label>
                <input class="tool-input" placeholder="Search questions…" [(ngModel)]="filterText" />
                <span class="card-count">{{ filteredQuestions().length }} question{{ filteredQuestions().length !== 1 ? 's' : '' }}</span>
              </div>
            </div>

            <div class="table-wrap">
              <div class="table-head">
                <span>Question</span>
                <span>Organization</span>
                <span>Enabled</span>
                <span>Deleted</span>
                <span>Actions</span>
              </div>

              @for (q of filteredQuestions(); track q.id) {
                <div class="table-row">
                  <span>{{ q.questionText }}</span>
                  <span>{{ getOrgName(q.organizationId) }}</span>
                  <span>{{ q.isEnabled ? 'Yes' : 'No' }}</span>
                  <span>{{ q.isDeleted ? 'Yes' : 'No' }}</span>
                  <span class="row-actions">
                    @if (q.isDeleted && canRestoreDeletedQuestions) {
                      <button class="btn btn--ghost" (click)="requestRestore(q.id)">Restore</button>
                    }
                    @if (q.isDeleted && canHardDeleteQuestions) {
                      <button class="btn btn--danger" (click)="requestHardDelete(q.id)">Hard Delete</button>
                    }
                  </span>
                </div>
              } @empty {
                <div class="empty">No questions found.</div>
              }
            </div>
          </div>
        </div>
      </section>

      @if (isConfirmModalOpen && pendingAction) {
        <div class="modal-backdrop" (click)="cancelAction()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-hd">
              <h2 class="modal-title">Confirm Action</h2>
            </div>
            <div class="modal-body">{{ getConfirmationText() }}</div>
            <div class="modal-ft">
              <button class="btn btn--ghost" (click)="cancelAction()">Cancel</button>
              <button class="btn btn--primary" (click)="confirmAction()">Confirm</button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1180px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.page-title{margin:.6rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card{border:1px solid var(--color-border);border-radius:.9rem;overflow:hidden;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card-hd{display:flex;justify-content:space-between;gap:.6rem;flex-wrap:wrap;border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.card-tools{display:flex;gap:.55rem;align-items:center;flex-wrap:wrap}.tool-toggle{display:flex;align-items:center;gap:.35rem;color:var(--color-muted);font-size:.82rem}.tool-input{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.38rem .5rem}.card-count{color:var(--color-muted);font-size:.9rem}.table-head,.table-row{display:grid;grid-template-columns:2fr 1fr .7fr .7fr 1.2fr;gap:.7rem;padding:.7rem 1rem;align-items:center}.table-head{font-size:.72rem;text-transform:uppercase;letter-spacing:.05em;color:var(--color-muted);border-bottom:1px solid var(--color-border)}.table-row{border-bottom:1px solid color-mix(in srgb,var(--color-border) 50%,transparent)}.row-actions{display:flex;gap:.45rem;justify-content:flex-start}.btn{border:1px solid transparent;border-radius:.45rem;cursor:pointer;font-size:.74rem;font-weight:600;padding:.28rem .55rem}.btn--ghost{background:color-mix(in srgb,var(--color-surface-2) 75%,transparent);border-color:var(--color-border);color:var(--color-muted)}.btn--danger{background:color-mix(in srgb,#ef4444 16%,transparent);border-color:color-mix(in srgb,#ef4444 55%,var(--color-border));color:#fca5a5}.btn--primary{background:#6366f1;color:#fff}.empty{padding:1rem;color:var(--color-muted)}.modal-backdrop{align-items:center;background:rgba(0,0,0,.55);bottom:0;display:flex;justify-content:center;left:0;position:fixed;right:0;top:0;z-index:1000}.modal{background:var(--color-bg-end);border:1px solid var(--color-border);border-radius:1rem;max-width:480px;width:100%}.modal-hd{border-bottom:1px solid var(--color-border);padding:1rem 1.2rem}.modal-title{margin:0;font-size:.96rem}.modal-body{padding:1rem 1.2rem;color:var(--color-text)}.modal-ft{border-top:1px solid var(--color-border);display:flex;gap:.5rem;justify-content:flex-end;padding:.9rem 1.2rem}`]
})
export class UtilitiesQuestionsPageComponent implements OnInit {
  private readonly questionService = inject(QuestionService);
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);

  readonly questions = signal<QuestionDto[]>([]);
  readonly organizations = signal<OrganizationDto[]>([]);
  filterText = '';
  showOnlyDeleted = false;

  canRestoreDeletedQuestions = false;
  canHardDeleteQuestions = false;

  isConfirmModalOpen = false;
  pendingAction: UtilityQuestionAction | null = null;
  pendingQuestionId: number | null = null;

  readonly filteredQuestions = computed(() => {
    const q = this.filterText.trim().toLowerCase();
    return this.questions()
      .filter(item => !this.showOnlyDeleted || !!item.isDeleted)
      .filter(item => !q || item.questionText.toLowerCase().includes(q) || this.getOrgName(item.organizationId).toLowerCase().includes(q))
      .sort((a, b) => a.questionText.localeCompare(b.questionText));
  });

  ngOnInit(): void {
    void this.initializeRoleContext();
    forkJoin({ questions: this.questionService.getAllForUtilities(), organizations: this.organizationService.getAll() }).subscribe({
      next: ({ questions, organizations }) => { this.questions.set(questions); this.organizations.set(organizations); }
    });
  }

  private async initializeRoleContext(): Promise<void> {
    const [isSiteAdmin, isOrgAdmin] = await Promise.all([
      this.authService.hasRoleAsync('SiteAdmin'),
      this.authService.hasRoleAsync('OrgAdmin')
    ]);

    window.setTimeout(() => {
      this.canRestoreDeletedQuestions = isSiteAdmin || isOrgAdmin;
      this.canHardDeleteQuestions = isSiteAdmin;
    }, 0);
  }

  requestRestore(questionId: number): void {
    this.requestAction('restore', questionId);
  }

  requestHardDelete(questionId: number): void {
    this.requestAction('hardDelete', questionId);
  }

  private requestAction(action: UtilityQuestionAction, questionId: number): void {
    this.pendingAction = action;
    this.pendingQuestionId = questionId;
    this.isConfirmModalOpen = true;
  }

  cancelAction(): void {
    this.isConfirmModalOpen = false;
    this.pendingAction = null;
    this.pendingQuestionId = null;
  }

  confirmAction(): void {
    if (!this.pendingAction || !this.pendingQuestionId) {
      return;
    }

    const questionId = this.pendingQuestionId;
    const action = this.pendingAction;
    this.cancelAction();

    if (action === 'restore') {
      this.questionService.restore(questionId).subscribe({
        next: (restored) => {
          this.questions.update(list => list.map(q =>
            q.id === restored.id
              ? { ...q, isDeleted: restored.isDeleted ?? false, isEnabled: restored.isEnabled ?? true }
              : q));
        },
        error: (err) => console.error('Failed to restore question:', err)
      });
      return;
    }

    this.questionService.hardDelete(questionId).subscribe({
      next: () => {
        this.questions.update(list => list.filter(q => q.id !== questionId));
      },
      error: (err) => console.error('Failed to hard delete question:', err)
    });
  }

  getConfirmationText(): string {
    if (this.pendingAction === 'restore') {
      return 'Are you sure you want to restore this question?';
    }

    return 'Are you sure you want to permanently delete this question?';
  }

  getOrgName(organizationId: number): string {
    return this.organizations().find(o => o.id === organizationId)?.displayName || `Org ${organizationId}`;
  }
}
